import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useRegion } from '../auth/RegionContext';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import type { Market } from '../dataProvider/types';
import { TransitionButton } from '../actions/TransitionButton';
import { formatMoney } from '../cashbacks/formatMoney';
import { JustifiedAction } from '../actions/JustifiedAction';
import { ATTR_STATUS_COLOR, ATTR_STATUS_LABEL } from './attributionState';
import type { AttributionBand as Band, OrphanRow } from './attributionModel';

/**
 * One full-width market band for the Manual attribution queue. The actionable
 * orphans (unattributed + needs_review) sit up top, each with its candidate clicks
 * and the guarded actions; a read-only history of worked orphans (credited +
 * rejected) sits beneath, so resolved items stay visible.
 *
 * The actions are sorted by direction of value (D-manual-attribution): CREDIT links
 * a member (super_admin), REJECT and FLAG are value-stopping / neutral (editor).
 * Crediting sets userId only and leaves status to the existing cashbacks:resolve
 * flow, so a credited orphan shows as "attributed, pending confirmation" until it
 * is separately confirmed. All actions route through the chokepoint, audited free.
 */

const MARKET_NAME: Record<Market, string> = {
  uk: 'United Kingdom',
  us: 'United States',
  ie: 'Ireland',
};

const StatusChip = ({ row }: { row: OrphanRow }) => (
  <Chip
    size="small"
    variant="outlined"
    color={ATTR_STATUS_COLOR[row.attributionStatus]}
    label={ATTR_STATUS_LABEL[row.attributionStatus]}
  />
);

const OrphanSummary = ({ row }: { row: OrphanRow }) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    useFlexGap
    flexWrap="wrap"
  >
    <Box sx={{ minWidth: 180 }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {row.storeName}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {row.market.toUpperCase()} |{' '}
        {formatMoney(row.grossAmount, row.currency)} |{' '}
        {row.occurredAt.slice(0, 10)}
      </Typography>
    </Box>
    <StatusChip row={row} />
  </Stack>
);

const OrphanCard = ({
  row,
  canCredit,
  canReject,
  canFlag,
}: {
  row: OrphanRow;
  canCredit: boolean;
  canReject: boolean;
  canFlag: boolean;
}) => {
  // previousData common to the credit / reject writes (audit diff baseline).
  const prevForCredit = {
    id: row.id,
    userId: null,
    attributionStatus: row.attributionStatus,
    matchedClickId: null,
    attributionNote: null,
  };

  return (
    <Card
      variant="outlined"
      data-testid={`attr-orphan-${row.id}`}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <OrphanSummary row={row} />

        {/* Candidate clicks: a display aid, never auto-credit (D-manual-attribution). */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Candidate clicks
          </Typography>
          {row.candidates.length === 0 ? (
            <Typography
              variant="body2"
              data-testid={`attr-nocandidates-${row.id}`}
            >
              No candidate clicks. Credit with a justification or reject.
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {row.candidates.map((c) => (
                <Stack
                  key={c.clickId}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  useFlexGap
                  flexWrap="wrap"
                  data-testid={`attr-candidate-${c.clickId}`}
                >
                  <Typography variant="body2" sx={{ minWidth: 200 }}>
                    {c.memberName}{' '}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      click {c.clickId} | {c.occurredAt.slice(0, 10)}
                    </Typography>
                  </Typography>
                  {canCredit && (
                    <TransitionButton
                      resource="transactions"
                      capability="attribution:credit"
                      id={row.id}
                      data={{
                        userId: c.userId,
                        attributionStatus: 'attributed',
                        matchedClickId: c.clickId,
                        attributionNote: null,
                      }}
                      previousData={prevForCredit}
                      label={`Credit to ${c.memberName}`}
                      color="success"
                      confirmTitle="Credit this transaction"
                      confirmContent="Link this transaction to the member. It rejoins the normal pending to confirmed flow and only counts once confirmed."
                      successMessage="Transaction attributed"
                    />
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          {canCredit && (
            <JustifiedAction
              resource="transactions"
              capability="attribution:credit"
              id={row.id}
              label="Credit with justification"
              color="success"
              dialogTitle="Credit without a click"
              dialogIntro="No candidate click fits. Enter the member id this transaction belongs to and why, for the audit trail."
              needsMember
              buildData={(justification, memberId) => ({
                userId: memberId,
                attributionStatus: 'attributed',
                matchedClickId: null,
                attributionNote: justification,
              })}
              previousData={prevForCredit}
              successMessage="Transaction attributed"
            />
          )}
          {canReject && (
            <JustifiedAction
              resource="transactions"
              capability="attribution:reject"
              id={row.id}
              label="Reject"
              color="error"
              dialogTitle="Reject as unattributable"
              dialogIntro="This transaction will never count for anyone. Record why."
              buildData={(justification) => ({
                attributionStatus: 'rejected',
                attributionNote: justification,
              })}
              previousData={{
                id: row.id,
                attributionStatus: row.attributionStatus,
                attributionNote: null,
              }}
              successMessage="Transaction rejected"
            />
          )}
          {canFlag && row.attributionStatus === 'unattributed' && (
            <TransitionButton
              resource="transactions"
              capability="attribution:flag"
              id={row.id}
              data={{ attributionStatus: 'needs_review' }}
              previousData={{
                id: row.id,
                attributionStatus: 'unattributed',
              }}
              label="Flag for review"
              color="warning"
              confirmTitle="Flag for review"
              confirmContent="Flag this transaction for a second look. No member is set and no value moves."
              successMessage="Flagged for review"
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const HistoryRow = ({ row }: { row: OrphanRow }) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    useFlexGap
    flexWrap="wrap"
    data-testid={`attr-history-${row.id}`}
    sx={{ py: 1 }}
  >
    <Box sx={{ minWidth: 180 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {row.storeName}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatMoney(row.grossAmount, row.currency)} |{' '}
        {row.occurredAt.slice(0, 10)}
      </Typography>
    </Box>
    <StatusChip row={row} />
    {row.memberName && (
      <Typography variant="body2">to {row.memberName}</Typography>
    )}
    {row.pendingConfirmation && (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label="pending confirmation"
        data-testid={`attr-pending-${row.id}`}
      />
    )}
    {row.attributionNote && (
      <Typography variant="caption" color="text.secondary">
        note: {row.attributionNote}
      </Typography>
    )}
    {row.matchedClickId != null && (
      <Typography variant="caption" color="text.secondary">
        click {row.matchedClickId}
      </Typography>
    )}
  </Stack>
);

export const AttributionBand = ({ band }: { band: Band }) => {
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  const ctx = { role, accessibleRegions };
  const canCredit = hasAccess(ctx, { capability: 'attribution:credit' });
  const canReject = hasAccess(ctx, { capability: 'attribution:reject' });
  const canFlag = hasAccess(ctx, { capability: 'attribution:flag' });

  return (
    <Card
      variant="outlined"
      data-testid={`attr-band-${band.market}`}
      sx={{ mb: 3 }}
    >
      <CardContent>
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          {MARKET_NAME[band.market]}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {band.actionable.length} to work ({band.counts.unattributed}{' '}
          unattributed, {band.counts.needs_review} needs review),{' '}
          {band.history.length} worked
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Queue
        </Typography>
        {band.actionable.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Nothing to work in this market.
          </Typography>
        ) : (
          band.actionable.map((row) => (
            <OrphanCard
              key={row.id}
              row={row}
              canCredit={canCredit}
              canReject={canReject}
              canFlag={canFlag}
            />
          ))
        )}

        {band.history.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Recently worked
            </Typography>
            {band.history.map((row) => (
              <HistoryRow key={row.id} row={row} />
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
};
